import React, { useEffect, useState, useContext } from 'react';
import { UserContext } from '../../contexts/UserContext';
import { getPageContainerStyles, getSearchFormStyles, getTableStyles } from '../../utils/styleUtils';
import { hasAdminAccess } from '../../utils/permissionConfig';
import { api } from '../../services/api';
import './StaffArtists.css';

const TEXT = {
  title: {
    ko: '레지던트 아티스트 관리',
    en: 'Staff Artists Management',
    fr: 'Gestion des artistes résidents'
  },
  loading: {
    ko: '로딩 중...',
    en: 'Loading...',
    fr: 'Chargement...'
  },
  noArtists: {
    ko: '레지던트 아티스트가 없습니다.',
    en: 'No staff artists found.',
    fr: 'Aucun artiste résident trouvé.'
  },
  deleteConfirm: {
    ko: '이 아티스트를 삭제하시겠습니까?',
    en: 'Are you sure you want to delete this artist?',
    fr: 'Êtes-vous sûr de vouloir supprimer cet artiste?'
  },
  delete: {
    ko: '삭제',
    en: 'Delete',
    fr: 'Supprimer'
  },
  // 테이블 헤더 다국어
  headers: {
    userId: {
      ko: '사용자 ID',
      en: 'User ID',
      fr: 'ID Utilisateur'
    },
    loginId: {
      ko: '로그인 ID',
      en: 'Login ID',
      fr: 'ID Connexion'
    },
    userName: {
      ko: '사용자명',
      en: 'User Name',
      fr: 'Nom Utilisateur'
    },
    email: {
      ko: '이메일',
      en: 'Email',
      fr: 'Email'
    },
    joinedAt: {
      ko: '가입일',
      en: 'Joined Date',
      fr: 'Date d\'inscription'
    },
    status: {
      ko: '상태',
      en: 'Status',
      fr: 'Statut'
    },
    location: {
      ko: '위치',
      en: 'Location',
      fr: 'Localisation'
    },
    level: {
      ko: '레벨',
      en: 'Level',
      fr: 'Niveau'
    },
    color: {
      ko: '색상',
      en: 'Color',
      fr: 'Couleur'
    }
  }
};

const StaffArtists = () => {
  const { userInfo } = useContext(UserContext);
  const [artists, setArtists] = useState([]);
  const [editing, setEditing] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const lang = userInfo?.preferred_language || 'ko';

  // 공통 스타일 가져오기
  const pageContainerStyles = getPageContainerStyles();
  const searchStyles = getSearchFormStyles();
  const tableStyles = getTableStyles();

  useEffect(() => {
    if (hasAdminAccess(userInfo)) {
      setLoading(true);
      api.get('/admin/staff-artists')
        .then((data) => {
          setArtists(Array.isArray(data?.users) ? data.users : []);
          setError(null);
        })
        .catch((err) => {
          setError(err?.data?.message || 'Failed to load artists.');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [userInfo]);

  const handleEdit = (userId, key, value) => {
    setLoading(true);
    api.put(`/admin/users/${userId}`, { [key]: value })
      .then(() => {
        setArtists((prev) =>
          prev.map((u) =>
            u.user_id === userId ? { ...u, [key]: value } : u
          )
        );
        setError(null);
      })
      .catch((err) => {
        setError(err?.data?.message || 'Failed to update artist.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDelete = (userId) => {
    if (!window.confirm(TEXT.deleteConfirm[lang])) return;
    setLoading(true);
    api.delete(`/admin/users/${userId}`)
      .then(() => {
        setArtists((prev) => prev.filter((u) => u.user_id !== userId));
        setError(null);
      })
      .catch((err) => {
        setError(err?.data?.message || 'Failed to delete artist.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const editableKeys = ['user_login_id', 'user_name', 'email', 'status', 'location', 'level', 'color'];

  return (
    <div className={pageContainerStyles.pageContainer} style={{
      height: '100%',
      overflow: 'auto',
      paddingBottom: '100px',
      boxSizing: 'border-box'
    }}>
      {/* 페이지 타이틀 */}
      <h1 className={pageContainerStyles.pageTitle}>
        {TEXT.title[lang]}
      </h1>

      {/* 로딩 및 에러 메시지 */}
      {loading && <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>{TEXT.loading[lang]}</p>}
      {error && <p style={{ color: 'red', margin: '0 0 15px 0', fontSize: '14px' }}>{error}</p>}

      {/* 테이블 컨테이너 */}
      <div className={tableStyles.tableContainer}>
        <table className={tableStyles.table}>
          <thead className={tableStyles.tableHeader}>
            <tr>
              <th>{TEXT.headers.userId[lang]}</th>
              <th>{TEXT.headers.loginId[lang]}</th>
              <th>{TEXT.headers.userName[lang]}</th>
              <th>{TEXT.headers.email[lang]}</th>
              <th>{TEXT.headers.joinedAt[lang]}</th>
              <th>{TEXT.headers.status[lang]}</th>
              <th>{TEXT.headers.location[lang]}</th>
              <th>{TEXT.headers.level[lang]}</th>
              <th>{TEXT.headers.color[lang]}</th>
              <th>{TEXT.delete[lang]}</th>
            </tr>
          </thead>
          <tbody className={tableStyles.tableBody}>
            {Array.isArray(artists) && artists.map((u) => (
              <tr key={u.user_id} className={tableStyles.tableRow}>
                <td>{u.user_id}</td>
                {['user_login_id', 'user_name', 'email', 'joined_at', 'status', 'location', 'level', 'color'].map((key) => (
                  <td
                    key={key}
                    style={{ cursor: editableKeys.includes(key) ? 'pointer' : 'default' }}
                    onClick={() =>
                      editableKeys.includes(key) &&
                      setEditing({ userId: u.user_id, key, value: u[key] })
                    }
                  >
                    {editing.userId === u.user_id && editing.key === key ? (
                      key === 'color' ? (
                        <input
                          type="color"
                          value={editing.value}
                          onChange={(e) =>
                            setEditing((prev) => ({ ...prev, value: e.target.value }))
                          }
                          onBlur={() => {
                            if (editing.value !== u[key]) {
                              handleEdit(u.user_id, key, editing.value);
                            }
                            setEditing({});
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              if (editing.value !== u[key]) {
                                handleEdit(u.user_id, key, editing.value);
                              }
                              setEditing({});
                            }
                            if (e.key === 'Escape') {
                              setEditing({});
                            }
                          }}
                          autoFocus
                          style={{ width: '100%' }}
                        />
                      ) : (
                        <input
                          value={editing.value}
                          onChange={(e) =>
                            setEditing((prev) => ({ ...prev, value: e.target.value }))
                          }
                          onBlur={() => {
                            if (editing.value !== u[key]) {
                              handleEdit(u.user_id, key, editing.value);
                            }
                            setEditing({});
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              if (editing.value !== u[key]) {
                                handleEdit(u.user_id, key, editing.value);
                              }
                              setEditing({});
                            }
                            if (e.key === 'Escape') {
                              setEditing({});
                            }
                          }}
                          autoFocus
                          style={{ width: '100%' }}
                        />
                      )
                    ) : key === 'joined_at' ? (
                      new Date(u[key]).toLocaleDateString('ko-KR')
                    ) : key === 'color' ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '20px', height: '20px', backgroundColor: u[key], border: '1px solid #ccc' }} />
                        <span>{u[key]}</span>
                      </div>
                    ) : (
                      u[key]
                    )}
                  </td>
                ))}
                <td>
                  <button
                    onClick={() => handleDelete(u.user_id)}
                    disabled={loading}
                    className={searchStyles.searchForm}
                    style={{ padding: '4px 8px', fontSize: '12px' }}
                  >
                    {TEXT.delete[lang]}
                  </button>
                </td>
              </tr>
            ))}
            {artists.length === 0 && !loading && (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  {TEXT.noArtists[lang]}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StaffArtists;
